/**
 * [비즈니스 목적]
 * React 애플리케이션의 전역 에러 경계 컴포넌트
 *
 * [워크플로우]
 * 1. 하위 컴포넌트에서 에러 발생 시 포착
 * 2. 에러 정보를 state에 저장
 * 3. Fallback UI 표시 (에러 메시지 및 스택 트레이스)
 * 4. 에러 로그를 콘솔에 출력 (추후 에러 리포팅 서비스 연동 가능)
 *
 * [주요 기능]
 * - React 컴포넌트 트리에서 발생하는 JavaScript 에러 포착
 * - 에러 발생 시 전체 앱 크래시 방지
 * - 개발자에게 유용한 에러 정보 표시
 * - 사용자에게 친화적인 에러 화면 제공
 *
 * [상태 관리]
 * - hasError: 에러 발생 여부
 * - error: Error 객체
 * - errorInfo: React 컴포넌트 스택 정보
 *
 * [사용 예시]
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * [참고]
 * - 이벤트 핸들러 에러는 포착하지 않음
 * - 비동기 코드 에러는 포착하지 않음
 * - 서버 사이드 렌더링 에러는 포착하지 않음
 */

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  /**
   * 에러 발생 시 state 업데이트
   * @param {Error} error - 발생한 에러 객체
   * @returns {Object} 업데이트된 state
   */
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  /**
   * 에러 로깅 및 상세 정보 저장
   * @param {Error} error - 에러 객체
   * @param {Object} errorInfo - React 컴포넌트 스택 정보
   */
  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div style={{ padding: '20px', backgroundColor: '#f8f8f8', minHeight: '100vh' }}>
          <h1>Something went wrong.</h1>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }

    return this.props.children; 
  }
}

export default ErrorBoundary;